import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import NavBar from '../NavBar/NavBar';
import ApplicationCard from '../../common/ApplicationCard';
import { usePermissions } from '../../../hooks/usePermissions';
import NavigationConstants from '../../../enums/NavigationConstants';
import './Home.css';
import FooterData from "../NavBar/FooterData";
import { getIdMeBaseApiUrl } from "../../../utils/UrlConstants";
import { useMsal } from "@azure/msal-react";
import { checkUserAuthentication, handleUnauthenticatedUser, isLogoutInProgress, isADLoginInProgress, setADLoginInProgress } from "../../../utils/authUtils";
import { fetchMicrosoftGraphGroups } from "../../../utils/groupUtils";
import { useIdentityAppService } from '../../../services/IdentityServices';

interface InternalApplication {
  title: string;
  description: string;
  url: string;
  permission: 'Excluded_Provider_Form' | 'APM_Form' | 'MCO_Form';
  isExternal?: boolean;
}

interface AppEnvironmentDto {
  appEnvCd: number;
  envName: string;
  envDesc?: string;
  appCd?: number;
  applicationName?: string;
  idpCd?: number;
  idpName?: string;
  ssoType?: string;
  appIdpIdentifier?: string;
  uri?: string;
  operationalFlag?: string;
  roles?: RoleDto[];
}

interface ApplicationDto {
  appCd: number;
  applicationName: string;
  applicationDesc: string;
  orgCd?: number;
  orgName?: string;
  appOwner?: string;
  operationalFlag?: string;
  allowExternalAccess?: string;
  allowInternalAccess?: string;
  environments?: AppEnvironmentDto[];
  roles?: RoleDto[];
}

interface RoleDto {
  roleCd: number;
  roleName: string;
  roleDesc?: string;
  appCd?: number;
  applicationName?: string;
  appEnvCd?: number;
  envName?: string;
  operationalFlag?: string;
  roleIdentifier?: string;
}

interface ApplicationListResponse {
  success: boolean;
  message: string;
  applications: ApplicationDto[];
  timestamp: string;
}

interface UserSearchResponse {
  success: boolean;
  message: string;
  users: {
    userId: number;
    userLogin: string;
    userEmail: string;
    firstName: string;
    lastName: string;
    applicationAdmin: boolean;
    [key: string]: any;
  }[];
  count: number;
  timestamp: string;
}

const InternalHome: React.FC = () => {
  const permissions = usePermissions();
  const { instance, accounts } = useMsal();
  const [sessionUserData, setSessionUserData] = useState<any>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [hasAdminRole, setHasAdminRole] = useState<boolean>(false);
  const [dynamicApplications, setDynamicApplications] = useState<ApplicationDto[]>([]);
  const [loadingApplications, setLoadingApplications] = useState(true);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const loadInProgressRef = useRef(false);
  const [allApplications, setAllApplications] = useState<ApplicationDto[]>([]);

  // Static applications - keeping for backwards compatibility
  const applications: InternalApplication[] = [];

  const staticApplications = [
    {
      title: "Application Owner Dashboard",
      description: "Click to Application Request Dashboard",
      url: "/admin-dashboard"
    },
    {
      title: "Request Dashboard",
      description: "Click to Request Access to Applications",
      url: "/internal-request-dashboard"
    }
  ];

  useEffect(() => {
    // If logout is in progress, don't check authentication - just redirect
    if (isLogoutInProgress()) {
      console.log("Logout in progress, redirecting to login...");
      window.location.href = "/";
      return;
    }

    // If AD login is in progress, wait for MSAL to complete authentication
    if (isADLoginInProgress()) {
      console.log("AD login in progress, waiting for MSAL authentication...");
      // Check periodically for MSAL accounts to be populated
      const adLoginCheckInterval = setInterval(() => {
        if (accounts && accounts.length > 0) {
          console.log("AD login completed, clearing flag and proceeding...");
          clearInterval(adLoginCheckInterval);
          setADLoginInProgress(false);
          setIsAuthenticating(false);

          // Check for existing user data in sessionStorage
          const existingUserData = sessionStorage.getItem('currentUserData');
          if (existingUserData) {
            try {
              const userData = JSON.parse(existingUserData);
              setSessionUserData(userData);
              setHasAdminRole(userData.applicationAdmin || false);
            } catch (error) {
              console.error("Error parsing existing user data:", error);
            }
          }

          searchCurrentUserAndStore();
        }
      }, 500);

      // Cleanup after 30 seconds max
      setTimeout(() => {
        clearInterval(adLoginCheckInterval);
        if (accounts && accounts.length > 0) {
          setADLoginInProgress(false);
          setIsAuthenticating(false);

          // Check for existing user data in sessionStorage
          const existingUserData = sessionStorage.getItem('currentUserData');
          if (existingUserData) {
            try {
              const userData = JSON.parse(existingUserData);
              setSessionUserData(userData);
              setHasAdminRole(userData.applicationAdmin || false);
            } catch (error) {
              console.error("Error parsing existing user data:", error);
            }
          }

          searchCurrentUserAndStore();
        } else {
          console.log("AD login timeout, clearing flag and handling unauthenticated user");
          setADLoginInProgress(false);
          handleUnauthenticatedUser();
        }
      }, 30000);

      return () => clearInterval(adLoginCheckInterval);
    }

    // Only check authentication if we have MSAL accounts
    if (!accounts || accounts.length === 0) {
      handleUnauthenticatedUser();
      return;
    }

    // Check for authentication tokens periodically until they're available
    const checkAuthPeriodically = () => {
      if (checkUserAuthentication()) {
        setIsAuthenticating(false);

        // Check for existing user data in sessionStorage
        const existingUserData = sessionStorage.getItem('currentUserData');
        if (existingUserData) {
          try {
            const userData = JSON.parse(existingUserData);
            setSessionUserData(userData);
            setHasAdminRole(userData.applicationAdmin || false);
          } catch (error) {
            console.error("Error parsing existing user data:", error);
          }
        }

        searchCurrentUserAndStore();
        return;
      }

      // If still no tokens after reasonable time, show error
      const maxWaitTime = 10000; // 10 seconds max
      const startTime = Date.now();

      const authCheckInterval = setInterval(() => {
        if (checkUserAuthentication()) {
          clearInterval(authCheckInterval);
          setIsAuthenticating(false);

          // Check for existing user data in sessionStorage
          const existingUserData = sessionStorage.getItem('currentUserData');
          if (existingUserData) {
            try {
              const userData = JSON.parse(existingUserData);
              setSessionUserData(userData);
              setHasAdminRole(userData.applicationAdmin || false);
            } catch (error) {
              console.error("Error parsing existing user data:", error);
            }
          }

          searchCurrentUserAndStore();
        } else if (Date.now() - startTime > maxWaitTime) {
          clearInterval(authCheckInterval);
          handleUnauthenticatedUser();
        }
      }, 500); // Check every 500ms

      return authCheckInterval;
    };

    const interval = checkAuthPeriodically();

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [accounts]);

  // Fetch Microsoft Graph groups
  const fetchGroups = async (attempt = 0): Promise<string[]> => {
    if (!accounts || accounts.length === 0) return [];
    const account = accounts[0];

    const tokenRequest = {
      scopes: ['User.Read', 'Group.Read.All'],
      account,
    };

    try {
      // Check if the token is already in sessionStorage
      let accessToken = sessionStorage.getItem('msalToken');
      if (!accessToken) {
        const response = await instance.acquireTokenSilent(tokenRequest);
        accessToken = response.accessToken;
        sessionStorage.setItem('msalToken', accessToken);
      }

      const groups = await fetchMicrosoftGraphGroups(accessToken);
      setUserGroups(groups);
      return groups;

      // const fetchMemberOf = async (url: string | URL | Request) => {
      //   const response = await fetch(url, {
      //     headers: {
      //       Authorization: `Bearer ${accessToken}`,
      //     },
      //   });
      //   return response.json();
      // };

      // const memberOfUrl = 'https://graph.microsoft.com/v1.0/me/memberOf';
      // let graphResponse = await fetchMemberOf(memberOfUrl);
      // let graphGroup = graphResponse.value;

      // while (graphResponse["@odata.nextLink"]) {
      //   try {
      //     graphResponse = await fetchMemberOf(graphResponse["@odata.nextLink"]);
      //     graphGroup = graphGroup.concat(graphResponse.value);
      //   } catch (error) {
      //     console.error('Failed to fetch additional data:', error);
      //     break;  // Exit the loop if there's an error
      //   }
      // }

      // graphGroup = graphGroup.filter((group: any) => group !== undefined);
      // const graphGroupsDisplayNames = graphGroup.map((group: {
      //   displayName: string;
      // }) => group.displayName.toLowerCase());

      // setUserGroups(graphGroupsDisplayNames);
      // return graphGroupsDisplayNames;
    } catch (error) {
      console.error('Error fetching Microsoft Graph groups:', error);

      if (attempt < 2) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchGroups(attempt + 1);
      }

      return [];
    }
  };

  // Fetch applications with roles from backend
  const fetchApplicationsWithRoles = async () => {
    try {
      // setLoadingApplications(true);

      const response = await useIdentityAppService().getInternalAccessWithRoles();

      if (response.ok) {
        const data: ApplicationListResponse = await response.json();
        if (data.success && data.applications) {
          return data.applications;
        } else {
          console.error('Failed to fetch applications:', data.message);
          return [];
        }
      } else {
        console.error('Failed to fetch applications:', response.status);
        return [];
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      return [];
    } finally {
      // setLoadingApplications(false);
    }
  };

  // Filter applications based on user groups
  const filterApplicationsByGroups = (apps: ApplicationDto[], userGroupNames: string[]): ApplicationDto[] => {
    if (!userGroupNames || userGroupNames.length === 0) {
      return apps;
    }

    return apps.filter(app => {
      if (!app.roles || app.roles.length === 0) {
        return false;
      }

      return app.roles.some(role => {
        if (role.roleName) {
          const roleNameLower = role.roleName.toLowerCase();
          return userGroupNames.some(groupName => groupName.toLowerCase() === roleNameLower);
        }
        return false;
      });
    });
  };

  // Load applications and user groups
  const loadDynamicApplications = async () => {
    if (!accounts || accounts.length === 0) return;
    if (loadInProgressRef.current) return;

    loadInProgressRef.current = true;
    setLoadingApplications(true);

    try {
      let graphGroups: string[] = [];
      let apps: ApplicationDto[] = [];

      for (let attempt = 0; attempt < 3; attempt++) {
        graphGroups = await fetchGroups(attempt);
        apps = await fetchApplicationsWithRoles();

        setAllApplications(apps);

        if ((graphGroups.length > 0 || apps.length > 0) || attempt === 2) {
          break;
        }

        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      const filteredApps = filterApplicationsByGroups(apps, graphGroups);
      setDynamicApplications(filteredApps);
      setUserGroups(graphGroups);
    } catch (error) {
      console.error('Error loading dynamic applications:', error);
      setDynamicApplications([]);
    } finally {
      loadInProgressRef.current = false;
      setLoadingApplications(false);
    }
  };

  // Load applications when component mounts and user is authenticated
  useEffect(() => {
    if (!isAuthenticating && accounts && accounts.length > 0) {
      loadDynamicApplications();
    }
  }, [isAuthenticating, accounts]);

  useEffect(() => {
    if (allApplications.length === 0) return;
    const filteredApps = filterApplicationsByGroups(allApplications, userGroups);
    setDynamicApplications(filteredApps);
  }, [allApplications, userGroups]);

  const searchCurrentUserAndStore = async () => {
    try {
      // Fetch user groups in parallel with user search
      fetchUserGroups();

      const email = accounts[0].username;

      // Build query parameters from token data
      const params = new URLSearchParams();
      if (email?.trim()) {
        const encodedEmail = btoa(email.trim());
        params.append('email', encodedEmail);
      }
      // Filter for Internal users only
      params.append('userType', 'Internal');

      const queryString = params.toString();
      const url = getIdMeBaseApiUrl() + `/user/search${queryString ? `?${queryString}` : ''}`;

      console.log("Searching for user with URL:", url);

      const response = await useIdentityAppService().getUserSearchByQuery(queryString ? `?${queryString}` : '');

      if (response.ok) {
        const data: UserSearchResponse = await response.json();
        console.log("User search response:", data);

        if (data.success && data.users && data.users.length > 0) {
          // Store the first matching user in session
          const userData = data.users[0];
          setSessionUserData(userData);

          // Store in sessionStorage for persistence across page reloads
          sessionStorage.setItem('currentUserData', JSON.stringify(userData));

          console.log("User data stored in session:", userData);

          // Set admin role based on backend response
          console.log("Full userData from backend:", userData);
          console.log("userData.applicationAdmin:", userData.applicationAdmin);
          console.log("Setting hasAdminRole to:", userData.applicationAdmin || false);
          setHasAdminRole(userData.applicationAdmin || false);
          console.log("User admin status from backend:", userData.applicationAdmin);
        } else {
          console.log("No user found in search results");
          setHasAdminRole(false);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Failed to search for user:", errorData);
        setHasAdminRole(false);
      }
    } catch (error) {
      console.error("Error searching for user:", error);
      setHasAdminRole(false);
    }
  };

  // Fetch groups for internal user
  const fetchUserGroups = async () => {
    try {
      if (accounts && accounts.length > 0) {
        const account = accounts[0];
        const tokenRequest = {
          scopes: ['User.Read', 'Group.Read.All'],
          account: account,
        };

        // Check if the token is already in sessionStorage
        let accessToken = sessionStorage.getItem('msalToken');
        if (!accessToken) {
          const response = await instance.acquireTokenSilent(tokenRequest);
          accessToken = response.accessToken;
          sessionStorage.setItem('msalToken', accessToken);
        }

        const groups = await fetchMicrosoftGraphGroups(accessToken);
        setUserGroups(groups);
        console.log('Fetched user groups for internal user:', groups);
        return groups;
      }
    } catch (error) {
      console.error('Error fetching user groups:', error);
      return [];
    }
  };

  // Convert dynamic applications to the format expected by ApplicationCard, filtering by user access
  const visibleApplications: InternalApplication[] = [];

  dynamicApplications.forEach(app => {
    if (app.environments && app.environments.length > 0) {
      // Filter environments based on user's role access
      const accessibleEnvironments = app.environments.filter(env => {
        if (!env.roles || env.roles.length === 0) {
          return false; // No roles defined for this environment
        }

        // Check if user has any role for this specific environment
        return env.roles.some(role => {
          if (role.roleName) {
            const roleNameLower = role.roleName.toLowerCase();
            return userGroups.some(groupName => groupName.toLowerCase() === roleNameLower);
          }
          return false;
        });
      });

      // Create separate tiles only for accessible environments
      accessibleEnvironments.forEach(env => {
        let url = env.uri || '#';

        // Only build OAuth URL if env.uri exists and is not null
        if (env.uri) {
          const clientId = '33b53cc1-fb46-4491-afba-0d5c8054244f';
          const redirectUri = env.uri;
          url = `https://login.microsoftonline.com/043207df-e689-4bf6-9020-01038f11f0b1/oauth2/v2.0/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=openid%20profile%20email&response_type=code&state=ad&response_mode=form_post`;
        }

        visibleApplications.push({
          title: `${app.applicationName} (${env.envName})`,
          description: env.envDesc || app.applicationDesc || 'Click to access this application',
          url: url,
          permission: 'MCO_Form' as const, // Default permission for now
          isExternal: true
        });
      });
    } else {
      // Fallback for applications without environments - check app-level roles
      if (app.roles && app.roles.length > 0) {
        const hasAccess = app.roles.some(role => {
          if (role.roleName) {
            const roleNameLower = role.roleName.toLowerCase();
            return userGroups.some(groupName => groupName.toLowerCase() === roleNameLower);
          }
          return false;
        });

        if (hasAccess) {
          visibleApplications.push({
            title: app.applicationName,
            description: app.applicationDesc || 'Click to access this application',
            url: '#',
            permission: 'MCO_Form' as const,
            isExternal: true
          });
        }
      }
    }
  });

  // Combine with static applications if any
  const allVisibleApplications = [...applications, ...visibleApplications];

  // Filter static applications based on admin role
  const visibleStaticApplications = staticApplications.filter(app => {
    // Show "Application Owner Dashboard" only if user has admin role
    if (app.title === "Application Owner Dashboard") {
      console.log("Filtering Application Owner Dashboard - hasAdminRole:", hasAdminRole);
      console.log("Current sessionUserData:", sessionUserData);
      return hasAdminRole;
    }
    // Show all other static applications
    return true;
  });

  // Show loading state while authenticating
  if (isAuthenticating) {
    return (
      <>
        <NavBar />
        <Box sx={{
          padding: 4,
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography variant="h5" sx={{ color: '#333' }}>
            {isADLoginInProgress() ? 'Authenticating with Azure AD...' : 'Loading...'}
          </Typography>
        </Box>
        <FooterData styles={true} />
      </>
    );
  }

  return (
    <>
      <NavBar />
      <Box sx={{
        padding: 4,
        minHeight: 'calc(100vh - 64px)',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        {/* My Applications Section */}
        {loadingApplications ? (
          <Box sx={{
            width: '100%',
            maxWidth: '900px',
            marginBottom: 6,
            textAlign: 'center',
            padding: 4
          }}>
            <Typography variant="h5" sx={{ color: '#333', marginBottom: 2 }}>
              Loading Applications...
            </Typography>
          </Box>
        ) : allVisibleApplications.length > 0 ? (
          <Box sx={{
            width: '100%',
            maxWidth: '900px', // Limit max width for better centering
            marginBottom: 6
          }}>
            <Typography
              variant="h4"
              sx={{
                textAlign: 'center',
                marginBottom: 4,
                fontWeight: 'bold',
                color: '#333'
              }}
            >
              My Applications
            </Typography>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)', // Exactly 3 columns
              gap: 3,
              justifyItems: 'center',
              // Responsive: reduce to 2 columns on medium screens, 1 on small
              '@media (max-width: 900px)': {
                gridTemplateColumns: 'repeat(2, 1fr)',
              },
              '@media (max-width: 600px)': {
                gridTemplateColumns: '1fr',
              }
            }}>
              {allVisibleApplications.map((app, index) => (
                <Box
                  key={`permission-${index}`}
                  sx={{
                    width: 280,
                    height: 200,
                    minWidth: 280,
                    minHeight: 200,
                    maxWidth: 280,
                    maxHeight: 200,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <ApplicationCard
                    title={app.title}
                    description={app.description}
                    url={app.url === '#' ? null : app.url}
                    isExternal={app.isExternal}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        ) : (

          <Box sx={{
            width: '100%',
            maxWidth: '900px',
            marginBottom: 6,
            textAlign: 'center',
            padding: 4,
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: 1
          }}>
            <Typography variant="h5" sx={{ color: '#666', marginBottom: 2 }}>
              No Applications Available
            </Typography>
            <Typography variant="body1" sx={{ color: '#999' }}>
              You don't have access to any applications at this time. Contact your administrator if you believe this is an error.
            </Typography>
          </Box>
        )}

        {/* User Management Section */}
        {visibleStaticApplications.length > 0 && (
          <Box sx={{
            width: '100%',
            maxWidth: '900px', // Limit max width for better centering
            marginBottom: 6
          }}>
            <Typography
              variant="h4"
              sx={{
                textAlign: 'center',
                marginBottom: 4,
                fontWeight: 'bold',
                color: '#333'
              }}
            >
              User Management
            </Typography>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)', // Exactly 3 columns
              gap: 3,
              justifyItems: 'center',
              // Responsive: reduce to 2 columns on medium screens, 1 on small
              '@media (max-width: 900px)': {
                gridTemplateColumns: 'repeat(2, 1fr)',
              },
              '@media (max-width: 600px)': {
                gridTemplateColumns: '1fr',
              }
            }}>
              {visibleStaticApplications.map((app, index) => (
                <Box
                  key={`static-${index}`}
                  sx={{
                    width: 280,
                    height: 200,
                    minWidth: 280,
                    minHeight: 200,
                    maxWidth: 280,
                    maxHeight: 200,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <ApplicationCard
                    title={app.title}
                    description={app.description}
                    url={app.url}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
      <FooterData
        styles={true}
      />
    </>
  );
};

export default InternalHome;